'use client'

import { useQuery } from '@tanstack/react-query'

import { useUserContext } from '@/context'

import { probeManageActivity } from './manageActivity.probe'
import { resolvePersonalWorkspaceOrganizationId } from './resolvePersonalWorkspaceOrganizationId'
import { shouldProbeManageActivity } from './shouldProbeManageActivity'

export const manageActivityProbeKey = (
  userId: string | undefined,
  organizationId: string | undefined,
) => ['manage-activity-probe', userId, organizationId] as const

export function useManageActivityProbe(options: { enabled?: boolean } = {}) {
  const {
    user,
    organizationMemberships,
    isLoadingUser,
    isClient,
    organizationScope,
  } = useUserContext()

  const shouldProbe =
    (options.enabled ?? true) &&
    isClient &&
    !isLoadingUser &&
    shouldProbeManageActivity({ user, organizations: organizationMemberships })
  const probeOrganizationId =
    organizationScope.activeOrganizationId ??
    resolvePersonalWorkspaceOrganizationId({
      organizationScope,
      teams: organizationMemberships,
    })

  const query = useQuery({
    queryKey: manageActivityProbeKey(user?.id, probeOrganizationId),
    queryFn: () =>
      probeManageActivity({
        userId: user!.id,
        organizationId: probeOrganizationId,
      }),
    enabled: shouldProbe && Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  return {
    shouldProbe,
    isPending: shouldProbe && query.isPending,
    hasBasicActivity: query.data === true,
    isError: query.isError,
  }
}
